import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authHeaders, axiosInstance, encodeValue, isEmptyData, logError } from '../../utils/helper';
import { uniqueID, ALL_CATEGORY } from '../../utils/constants';
import qs from 'qs';

const initialState = {
	list: [],
	outletBrand: {},

	details: null,

	tags: [],
	isLoadingTags: false,
	selectedTag: ALL_CATEGORY,

	products: {},
	totalPages: 0,
	page: 1,
	total: 0,
	limit: 10,
	loading: false,

	isLoadingOtherLocation: false,
	isOpenOtherLocation: false,

	selectedStoreIDForLocation: null,
	isLoadMore: false,
	selectedStoreID: null,

	brandID: null,
	outletByLocation: [],
	totalPagesLocation: 0,
	pageLocation: 1,
	totalLocation: 0,
	limitLocation: 10,

	selectedBrandID: null,
};

export const handleGetStoreDetail = createAsyncThunk('outlet/handleGetStoreDetail', async ({ storeID, companyID, latitude, longitude, brandID }) => {
	let url = `store/storeDetails?unquieid=${uniqueID}`;
	if (companyID) {
		url += `&company_id=${companyID}`;
	}
	if (/^\d+$/.test(storeID)) {
		url += `&storeID=${storeID}`;
	} else if (!isEmptyData(storeID)) {
		url += `&storeSlug=${storeID}`;
	}

	if (brandID) {
		url += `&brandID=${brandID}`;
	}

	if (latitude && longitude) {
		url += `&latitude=${latitude}&longitude=${longitude}`;
	}

	const response = await axiosInstance.get(url, authHeaders());

	if (response?.data?.status === 'ok') {
		return response?.data?.result || {};
	}
	return {};
});

export const handleOutletByLocation = createAsyncThunk('outlet/handleOutletByLocation', async ({ item, latitude, longitude, page = 1, limit = 10, isLoadMore = false }, { getState }) => {
	let url = `store/listStore?brand_id=${item?.brandID}&latitude=${latitude}&longitude=${longitude}&page=${page}&limit=${limit}`;
	if (item?.companyID) {
		url += `&company_id=${item?.companyID}`;
	}
	const response = await axiosInstance.get(url, authHeaders());

	const currentState = getState().outlet ?? [];

	if (response?.data?.status === 'ok') {
		const result = response?.data?.result || [];
		const newItems = isLoadMore ? result.filter((item) => !currentState.outletByLocation.some((outlet) => outlet.storeID === item.storeID)) : result;

		return {
			brandID: item?.brandID,
			outlets: isLoadMore ? [...currentState.outletByLocation, ...newItems] : result,
			totalPagesLocation: response?.data?.total_pages,
			totalLocation: response?.data?.total,
			pageLocation: page,
			limitLocation: limit,
		};
	}
	return { brandID: item?.brandID, outlets: [] };
});

export const handleFavorite = createAsyncThunk('outlet/handleFavorite', async ({ isFavorite, dataFavoritePost }) => {
	const url = `store/${isFavorite ? 'removeFavourite' : 'addFavourite'}`;
	const response = await axiosInstance.post(url, qs.stringify(dataFavoritePost), authHeaders());
	return response.data;
});

export const handleGetTags = createAsyncThunk('outlet/handleGetTags', async ({ uniqueID, storeID }) => {
	const url = `catalogs/listcategory?unquieid=${uniqueID}&storeID=${storeID}`;
	const res = await axiosInstance.get(url, authHeaders());

	const updatedTags = [ALL_CATEGORY, ...(res?.data?.result || [])];
	return updatedTags;
});

const deepClone = (obj) => {
	if (typeof structuredClone === 'function') {
		return structuredClone(obj);
	}
	try {
		return JSON.parse(JSON.stringify(obj));
	} catch (error) {
		console.error('Cloning error:', error);
		return obj; // Fallback jika cloning gagal
	}
};

export const handleGetOutletList = createAsyncThunk('outlet/handleGetOutletList', async ({ page = 1, limit = 10, isLoadMore = false, latitude, longitude, tag = null }, { getState }) => {
	let url = `store/listStore?unquieid=${uniqueID}&latitude=${latitude}&longitude=${longitude}&page=${page}&limit=${limit}`;
	if (!isEmptyData(tag)) {
		url += `&tag=${tag}`;
	}
	const response = await axiosInstance.get(url, authHeaders());
	const { status, result = [], total_pages = 0, total = 0 } = response?.data || {};

	const currentState = getState().outlet;

	if (status === 'ok') {
		const newItems = isLoadMore ? result.filter((item) => !currentState.list.some((outlet) => outlet.storeID === item.storeID)) : result;

		return {
			list: isLoadMore ? [...currentState.list, ...newItems] : result,
			totalPages: total_pages,
			page,
			total: Number(total),
			limit,
			isLoadMore,
		};
	} else {
		return {
			list: [],
			totalPages: 0,
			page,
			total: 0,
			limit,
			isLoadMore,
		};
	}
});

const outlet = createSlice({
	name: 'outlet',
	initialState,
	reducers: {
		clearOutlets: () => initialState,
		setOutlet: (state, action) => {
			state.list = deepClone(action.payload.list);
			state.loading = action.payload.loading || false;
		},
		setOutletBrand: (state, action) => {
			const { brandID, outlets } = action.payload;
			state.outletBrand[brandID] = outlets;
		},
		setOutletDetail: (state, action) => {
			state.details = action.payload;
		},
		setOutletTags: (state, action) => {
			state.tags = action.payload;
		},
		setLoadingOtherLocation: (state, action) => {
			state.isLoadingOtherLocation = action.payload;
		},
		setOpenOtherLocation: (state, action) => {
			state.isOpenOtherLocation = action.payload;
		},
		setIsLoadMore: (state, action) => {
			state.isLoadMore = action.payload;
		},
		setSelectedBrandID: (state, action) => {
			state.selectedBrandID = action.payload;
		},
		resetOutletPagination: (state) => {
			state.totalPages = 0;
			state.total = 1;
			state.page = 1;
			state.limit = 10;
			state.isLoadMore = false;
			state.list = [];
			state.loading = true;
		},
		resetOutletByLocation: (state) => {
			state.brandID = null;
			state.outletByLocation = [];
			state.selectedStoreID = null;
		},
		resetLoadingOtherLocation: (state) => {
			state.isLoadingOtherLocation = false;
			state.isOpenOtherLocation = false;
		},
		setSelectedTag: (state, action) => {
			state.selectedTag = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(handleGetOutletList.pending, (state, action) => {
				if (action.meta.arg.isLoadMore) {
					return {
						...state,
						...action.meta.arg,
					};
				}
			})
			.addCase(handleGetOutletList.fulfilled, (state, action) => {
				state.loading = false;
				state.isLoadMore = false;
				state.list = action.payload.list;
				state.totalPages = action.payload.totalPages;
				state.total = action.payload.total;
			})
			.addCase(handleGetOutletList.rejected, (state, action) => {
				state.isLoadMore = false;
				state.loading = false;

        // logError(new Error(action.error?.message), {
        //   feature: "handleGetOutletList",
        //   actionType: action.type,
        // });
      })
      .addCase(handleOutletByLocation.pending, (state, action) => {
        state.isLoadingOtherLocation = true;
        state.isOpenOtherLocation = true;
        state.brandID = action.meta.arg?.item?.brandID ?? null;
        state.selectedStoreID = action.meta.arg?.item?.storeID ?? null;
      })
      .addCase(handleOutletByLocation.fulfilled, (state, action) => {
        const {
          outlets,
          pageLocation,
          totalPagesLocation,
          limitLocation,
          totalLocation,
        } = action.payload;
        state.isLoadingOtherLocation = false;
        state.outletByLocation = outlets;
        state.pageLocation = pageLocation;
        state.totalPagesLocation = totalPagesLocation;
        state.limitLocation = limitLocation;
        state.totalLocation = totalLocation;
      })
      .addCase(handleOutletByLocation.rejected, (state, action) => {
        state.isLoadingOtherLocation = false;

        // logError(new Error(action.error?.message), {
        //   feature: "handleOutletByLocation",
        //   actionType: action.type,
        // });
      })
      .addCase(handleGetStoreDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(handleGetStoreDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.details = action.payload;
      })
      .addCase(handleGetStoreDetail.rejected, (state, action) => {
        state.loading = false;

        // logError(new Error(action.error?.message), {
        //   feature: "handleGetStoreDetail",
        //   actionType: action.type,
        // });
      })
			.addCase(handleFavorite.pending, (state) => {
				state.loading = true;
			})
			.addCase(handleFavorite.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(handleFavorite.rejected, (state, action) => {
				state.loading = false;

				// logError(new Error(action.error?.message), {
				// 	feature: 'handleGetStoreDetail',
				// 	actionType: action.type,
				// });
			})
			.addCase(handleGetTags.pending, (state) => {
				state.isLoadingTags = true;
			})
			.addCase(handleGetTags.fulfilled, (state, action) => {
				state.isLoadingTags = false;
				state.tags = action.payload;
			})
			.addCase(handleGetTags.rejected, (state, action) => {
				state.isLoadingTags = false;

				// logError(new Error(action.error?.message), {
				// 	feature: 'handleGetTags',
				// 	actionType: action.type,
				// });
			});
  },
});

export const {
	clearOutlets,
	setOutlet,
	setOutletBrand,
	setOutletDetail,
	setOutletTags,
	setOutletProducts,
	setLoadingOtherLocation,
	setOpenOtherLocation,
	setIsLoadMore,
	resetOutletPagination,
	resetOutletByLocation,
	resetLoadingOtherLocation,
	setSelectedBrandID,
  setSelectedTag,
} = outlet.actions;
export default outlet.reducer;
